import React from 'react';

/**
 * Holds a value on screen for a minimum time before letting it change.
 *
 * The agent's steps arrive in bursts -- two tool calls a second apart make the
 * status line flicker through messages faster than anyone can read them, which
 * reads as noise rather than progress.
 *
 * When the underlying value changes too soon, the switch is deferred until the
 * current one has had its time. Crucially, what gets shown then is whatever is
 * current at THAT moment, not the value that was pending when the timer was
 * set: a phase that came and went inside the window is simply skipped, and the
 * display always converges on the truth rather than replaying a backlog.
 */
export function useMinimumDwell<T>(value: T, minMs: number): T {
  const [shown, setShown] = React.useState<T>(value);

  // When the DISPLAYED value last changed. The deadline is anchored to this,
  // not to the last update, so a burst of changes cannot keep pushing it back.
  const shownAt = React.useRef<number>(Date.now());

  // Always the newest value, so a deferred switch can read it rather than
  // closing over a stale one.
  const latest = React.useRef<T>(value);
  latest.current = value;

  React.useEffect(() => {
    if (value === shown) return undefined;

    const elapsed = Date.now() - shownAt.current;
    if (elapsed >= minMs) {
      shownAt.current = Date.now();
      setShown(value);
      return undefined;
    }

    const id = window.setTimeout(() => {
      shownAt.current = Date.now();
      setShown(latest.current);
    }, minMs - elapsed);

    return () => window.clearTimeout(id);
  }, [value, shown, minMs]);

  return shown;
}

export default useMinimumDwell;
